import { useCallback, useRef } from 'react'

/** Сколько ещё не пускаем повторный платёж после удачно созданного счёта. */
const COOLDOWN_MS = 3000

export interface DoubleSubmitGuard {
  /** Занимаем замок. `false` — платёж уже создаётся, обработчик надо прервать. */
  tryStart: () => boolean
  /** Отпускаем замок. При удаче держим паузу, при ошибке пускаем сразу. */
  finish: (succeeded: boolean) => void
}

/**
 * Замок от двойного платежа.
 *
 * Проверять `mutation.isLoading` недостаточно: это состояние React, и оно
 * становится `true` только к следующей отрисовке. Два быстрых тапа по кнопке
 * успевают пройти до неё, и на бэке появляются два счёта в одну секунду —
 * люди получали двойное списание.
 *
 * Признак живёт в ref и выставляется синхронно, прямо в обработчике клика, до
 * любых await. После удачного создания счёта держим короткую паузу: второй тап
 * нередко приходит уже после ответа на первый, когда замок сам по себе снят.
 */
export const useDoubleSubmitGuard = (): DoubleSubmitGuard => {
  const isBusyRef = useRef(false)
  const lockedUntilRef = useRef(0)

  const tryStart = useCallback(() => {
    if (isBusyRef.current) return false
    if (Date.now() < lockedUntilRef.current) return false
    isBusyRef.current = true
    return true
  }, [])

  const finish = useCallback((succeeded: boolean) => {
    isBusyRef.current = false
    lockedUntilRef.current = succeeded ? Date.now() + COOLDOWN_MS : 0
  }, [])

  return { tryStart, finish }
}
